import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { AdminPage, AdminState, isForbidden } from '@/admin/AdminShell'
import { StateView } from '@/card/record'
import { fetchInsights, type Insights } from './supportTriageApi'

/**
 * Сводка по обращениям за период (#132): темы и всплески.
 *
 * - **Темы — категории разбора ИИ**, а не ключевые слова из текста: слова из тем и писем
 *   вынесли бы на экран руководителя имена и номера клиентов, а категории сравнимы от месяца к
 *   месяцу и не несут ПДн. Неразобранные обращения — отдельной строкой, не размазаны по темам.
 * - **Всплеск считает сервер** (порог и кратность приходят в ответе): вторая копия правила на
 *   клиенте разошлась бы с первой при первой правке.
 * - **Только видимые смотрящему обращения**: менеджер с областью «своё» видит сводку своих
 *   обращений, а не цифры компании.
 */
export function SupportInsightsPage() {
  const { t } = useApp()
  const [draft, setDraft] = useState<{ from: string; to: string }>({ from: '', to: '' })
  const [period, setPeriod] = useState<{ from: string | null; to: string | null }>({ from: null, to: null })
  const insights = useQuery({
    queryKey: ['support-insights', period.from, period.to],
    queryFn: ({ signal }) => fetchInsights(period.from, period.to, signal),
    retry: false,
  })

  return (
    <AdminPage sectionKey="nav.section.sales" titleKey="nav.supportInsights" forbidden={isForbidden(insights.error)}>
      <div className="row" style={{ gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
        <label className="stack" style={{ gap: 4 }}>
          <span className="t-sm">{t('insights.from')}</span>
          <input className="input" type="date" aria-label={t('insights.from')} value={draft.from}
                 onChange={(event) => setDraft({ ...draft, from: event.target.value })} />
        </label>
        <label className="stack" style={{ gap: 4 }}>
          <span className="t-sm">{t('insights.to')}</span>
          <input className="input" type="date" aria-label={t('insights.to')} value={draft.to}
                 onChange={(event) => setDraft({ ...draft, to: event.target.value })} />
        </label>
        <button type="button" className="btn btn-secondary"
                onClick={() => setPeriod({ from: draft.from || null, to: draft.to || null })}>
          {t('insights.apply')}
        </button>
      </div>
      <AdminState isLoading={insights.isLoading} error={insights.error} onRetry={() => void insights.refetch()}>
        {insights.data && <Summary data={insights.data} />}
      </AdminState>
    </AdminPage>
  )
}

function Summary({ data }: { data: Insights }) {
  const { t, locale } = useApp()
  const lines = data.categories ?? []
  const spikes = data.spikes ?? []
  const tones = data.tones ?? {}
  const percent = (value: number) => `${Math.round(value * 100)}%`
  const label = (code: string) => t(`value.support.ticket.triage_category.${code}`)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <p className="page-lead">{t('insights.intro', { min: data.spikeMinCount, ratio: data.spikeRatio.toLocaleString(locale) })}</p>
      <dl className="row" style={{ gap: 24, margin: 0 }} aria-label={t('insights.title')}>
        <div>
          <dt className="muted t-sm">{t('insights.total')}</dt>
          <dd className="h2" style={{ margin: 0 }} data-testid="insights-total">{data.total}</dd>
        </div>
        <div>
          <dt className="muted t-sm">{t('insights.previous', { from: data.previousFrom, to: data.previousTo })}</dt>
          <dd className="h2" style={{ margin: 0 }}>{data.previousTotal}</dd>
        </div>
        <div>
          <dt className="muted t-sm">{t('insights.urgentCount')}</dt>
          <dd className="h2" style={{ margin: 0 }}>{data.urgent}</dd>
        </div>
        <div>
          <dt className="muted t-sm">{t('insights.unread')}</dt>
          <dd className="h2" style={{ margin: 0 }} data-testid="insights-unread">{data.unread}</dd>
        </div>
      </dl>

      <p role="status" data-testid="insights-spikes" className={spikes.length > 0 ? 'strong danger' : 'muted'}>
        {spikes.length > 0 ? t('insights.spikes', { list: spikes.map(label).join(', ') }) : t('insights.noSpikes')}
      </p>

      {lines.length === 0 ? (
        <StateView boxed kind="empty" title={t('insights.empty')} />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl" aria-label={t('insights.themes')}>
            <thead>
              <tr>
                <th>{t('insights.col.category')}</th>
                <th className="num">{t('insights.col.count')}</th>
                <th className="num">{t('insights.col.previous')}</th>
                <th className="num">{t('insights.col.share')}</th>
                <th className="num">{t('insights.col.harsh')}</th>
                <th className="num">{t('insights.col.urgent')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.category} data-category={line.category}>
                  <td>
                    {label(line.category)}
                    {line.spike && <span className="badge b-danger" style={{ marginLeft: 6 }}>{t('insights.spike')}</span>}
                  </td>
                  <td className="num">{line.count}</td>
                  <td className="num">{line.previous}</td>
                  <td className="num">{percent(line.share)}</td>
                  <td className="num">{line.harsh}</td>
                  <td className="num">{line.urgent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section aria-label={t('insights.tones')}>
        <h2 className="h3">{t('insights.tones')}</h2>
        <ul className="row" style={{ gap: 16, listStyle: 'none', padding: 0 }}>
          {Object.entries(tones).map(([tone, count]) => (
            <li key={tone}>
              {t(`value.support.ticket.triage_tone.${tone}`)}: <span className="strong">{count}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
